import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../helpers/AuthContext';
import { api } from '../helpers/apiConfig';
import '../styles/ClaimCard.css';

// Estados posibles de la pantalla mientras se resuelve la tarjeta
const STATUS = {
  LOADING: 'loading',
  NOT_FOUND: 'not_found',
  CLAIMED: 'claimed',
  UNCLAIMED: 'unclaimed',
  CONFIRM: 'confirm',
  CLAIMING: 'claiming',
  CLAIM_ERROR: 'claim_error'
};

const ClaimCard = () => {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [status, setStatus] = useState(STATUS.LOADING);
  const [username, setUsername] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const checkCard = async () => {
      setStatus(STATUS.LOADING);
      try {
        const res = await fetch(api(`/api/cards/${cardId}`));
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok) {
          setStatus(STATUS.NOT_FOUND);
          return;
        }

        if (data.status === 'claimed') {
          setUsername(data.username);
          setStatus(STATUS.CLAIMED);
        } else if (isAuthenticated) {
          // Nunca asociar en silencio: siempre pedir confirmación explícita,
          // por si el dispositivo tiene la sesión de otra persona.
          setStatus(STATUS.CONFIRM);
        } else {
          setStatus(STATUS.UNCLAIMED);
        }
      } catch (err) {
        if (!cancelled) setStatus(STATUS.NOT_FOUND);
      }
    };

    checkCard();
    return () => { cancelled = true; };
  }, [cardId, isAuthenticated]);

  const handleConfirmClaim = async () => {
    setStatus(STATUS.CLAIMING);
    setErrorMsg(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(api(`/api/cards/${cardId}/claim`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token || ''}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errorMessages = {
          'AlreadyClaimed': 'Esta tarjeta ya está asociada a otra cuenta.',
          'SessionInvalid': 'Tu sesión ya no es válida. Inicia sesión nuevamente.'
        };
        setErrorMsg(errorMessages[data.errorcode] || data.detail || 'No se pudo asociar la tarjeta');
        if (data.errorcode === 'SessionInvalid') {
          logout();
          setStatus(STATUS.UNCLAIMED);
        } else {
          setStatus(STATUS.CLAIM_ERROR);
        }
        return;
      }
      setUsername(data.username);
      setStatus(STATUS.CLAIMED);
    } catch (err) {
      setErrorMsg('Error de conexión. Intenta nuevamente.');
      setStatus(STATUS.CLAIM_ERROR);
    }
  };

  const handleUseAnotherAccount = () => {
    logout();
    setErrorMsg(null);
    setStatus(STATUS.UNCLAIMED);
  };

  if (status === STATUS.CLAIMED && username) {
    return <Navigate to={`/user/${username}`} replace />;
  }

  return (
    <div className="claim-container">
      <div className="claim-card">
        {status === STATUS.LOADING && (
          <p className="claim-message">Verificando tarjeta...</p>
        )}

        {status === STATUS.NOT_FOUND && (
          <>
            <h2>Tarjeta no reconocida</h2>
            <p className="claim-message">
              Este código no corresponde a ninguna tarjeta Tappy. Si crees que es un error,
              contáctanos.
            </p>
          </>
        )}

        {status === STATUS.CLAIMING && (
          <p className="claim-message">Asociando la tarjeta a tu cuenta...</p>
        )}

        {status === STATUS.CONFIRM && (
          <>
            <h2>Activa tu tarjeta Tappy</h2>
            <p className="claim-message">
              Esta tarjeta se asociará permanentemente a la cuenta <strong>@{user?.username}</strong>.
            </p>
            {errorMsg && <div className="claim-error">{errorMsg}</div>}
            <div className="claim-actions">
              <button className="claim-btn claim-btn-primary" onClick={handleConfirmClaim}>
                Asociar a @{user?.username}
              </button>
              <button className="claim-btn claim-btn-secondary" onClick={handleUseAnotherAccount}>
                Usar otra cuenta
              </button>
            </div>
          </>
        )}

        {(status === STATUS.UNCLAIMED || status === STATUS.CLAIM_ERROR) && (
          <>
            <h2>Activa tu tarjeta Tappy</h2>
            <p className="claim-message">
              Esta tarjeta todavía no está asociada a ninguna cuenta. Inicia sesión o
              crea tu perfil para vincularla.
            </p>
            {errorMsg && <div className="claim-error">{errorMsg}</div>}
            <div className="claim-actions">
              <button
                className="claim-btn claim-btn-primary"
                onClick={() => navigate('/login', { state: { from: `/c/${cardId}` } })}
              >
                Iniciar sesión
              </button>
              <button
                className="claim-btn claim-btn-secondary"
                onClick={() => navigate('/register', { state: { from: `/c/${cardId}` } })}
              >
                Crear cuenta
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClaimCard;
